import React, { useRef, useCallback } from 'react';

import { InfoCardVariants } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';

import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import {
  ProjectItem,
  ClusterConfigItem,
  SyncPolicyItem,
  AppMetadataItem,
  SourceItem,
  getEntitySource,
} from './DaasApplicationContentCard';
import { ContentSection } from './DaasContentSection';
import { CopyButton } from './DaasInlineField';
import { DaasSectionItem } from './DaasSectionItem';
import { useStyles } from './styles';
import { ApplicationMetadata, ClusterConfig, SyncPolicy } from './types';

interface CodeSnippetBlockProps {
  base64: string;
}

const CodeSnippetBlock = ({ base64 }: CodeSnippetBlockProps) => {
  const classes = useStyles();
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const preRef = useRef<HTMLPreElement>(null);

  let decoded: string | null;
  try {
    decoded = atob(base64);
  } catch {
    decoded = null;
  }

  const handleScroll = useCallback(() => {
    if (!preRef.current) return;
    preRef.current.classList.add(classes.codeBlockScrolling);
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      preRef.current?.classList.remove(classes.codeBlockScrolling);
    }, 1000);
  }, [classes.codeBlockScrolling]);

  if (decoded === null) {
    return (
      <DaasSectionItem>
        <Typography variant="body1">
          Unable to decode content (invalid base64)
        </Typography>
      </DaasSectionItem>
    );
  }

  return (
    <DaasSectionItem>
      <Box position="relative" width="100%">
        <Box position="absolute" top={4} right={4} zIndex={1}>
          <CopyButton text={decoded} />
        </Box>
        <pre ref={preRef} className={classes.codeBlock} onScroll={handleScroll}>
          {decoded}
        </pre>
      </Box>
    </DaasSectionItem>
  );
};

export interface DaasApplicationSetContentCardProps {
  variant?: InfoCardVariants;
}

export const DaasApplicationSetContentCard = ({
  variant,
}: DaasApplicationSetContentCardProps) => {
  const { entity } = useEntity();
  const classes = useStyles();

  const cardClass =
    variant === 'gridItem' ? classes.gridItemCard : classes.fullHeightCard;
  const cardContentClass =
    variant === 'gridItem'
      ? classes.gridItemCardContent
      : classes.fullHeightCardContent;

  const clusterConfig =
    (entity.spec?.clusterConfig as unknown as ClusterConfig) || null;
  const source = getEntitySource(entity);
  const syncPolicy = (entity.spec?.syncPolicy as unknown as SyncPolicy) || null;
  const applicationMetadata =
    (entity.spec?.applicationMetadata as unknown as ApplicationMetadata) ||
    null;
  const generators = (entity.spec?.generators as string) || null;
  const templatePatch = (entity.spec?.templatePatch as string) || null;

  return (
    <Card className={cardClass}>
      <CardHeader
        title="Application Set Details"
        className={classes.cardHeader}
      />
      <Divider />
      <CardContent className={cardContentClass}>
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <ContentSection label="Project" gridSizes={{ xs: 12 }}>
              <ProjectItem project={entity} />
            </ContentSection>
          </Grid>

          <Grid item md={6} xs={12}>
            <ContentSection label="Application Metadata" gridSizes={{ xs: 12 }}>
              {applicationMetadata ? (
                <AppMetadataItem metadata={applicationMetadata} />
              ) : (
                <Typography variant="body1">No metadata configured</Typography>
              )}
            </ContentSection>
          </Grid>

          <Grid item md={6} xs={12}>
            <Grid container spacing={3}>
              <ContentSection label="Cluster" gridSizes={{ xs: 12 }}>
                {clusterConfig ? (
                  <ClusterConfigItem cluster={clusterConfig} />
                ) : (
                  <Typography variant="body1">No cluster configured</Typography>
                )}
              </ContentSection>

              <ContentSection label="Repository" gridSizes={{ xs: 12 }}>
                {source ? (
                  <SourceItem source={source} />
                ) : (
                  <Typography variant="body1">
                    No repository configured
                  </Typography>
                )}
              </ContentSection>
            </Grid>
          </Grid>

          <Grid item md={6} xs={12}>
            <ContentSection label="Sync Policy" gridSizes={{ xs: 12 }}>
              {syncPolicy ? (
                <SyncPolicyItem syncPolicy={syncPolicy} />
              ) : (
                <Typography variant="body1">
                  No sync policy configured
                </Typography>
              )}
            </ContentSection>
          </Grid>

          <Grid item md={6} xs={12}>
            <ContentSection label="Generators" gridSizes={{ xs: 12 }}>
              {generators ? (
                <CodeSnippetBlock base64={generators} />
              ) : (
                <Typography variant="body1">
                  No generators configured
                </Typography>
              )}
            </ContentSection>
          </Grid>

          <Grid item md={6} xs={12}>
            <ContentSection label="Template Patch" gridSizes={{ xs: 12 }}>
              {templatePatch ? (
                <CodeSnippetBlock base64={templatePatch} />
              ) : (
                <Typography variant="body1">
                  No template patch configured
                </Typography>
              )}
            </ContentSection>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
